/**
 * NPC in-world placement (E2.4.S3) — consume the join-only `helloAck.npcs` the
 * client currently ignores and stand each resolvable NPC at its (x, z). NPCs are
 * STATIC join-only zone data (no tick presence, no movement/anim on the wire), so —
 * unlike the self/remote player (`PlayerRigRenderer`) and enemy (`CreatureRenderer`)
 * upgrade-in-place renderers — this is a fresh PLACER: `place()` the zone's NPCs on
 * join, `clear()`/rebuild on a zone change (ridden off `Game.onReady`), `dispose()`
 * on teardown.
 *
 * It SHARES the rig-avatar machinery (the story's dependency contract): the rig
 * fallback path delegates to the S1 `RigAvatarBuilder.build(DEFAULT_APPEARANCE)` — the
 * exact resolve → instantiate(`body-base`, lod0) → morph → wardrobe unit a default
 * player gets — so an NPC renders as a default-appearance humanoid, never a
 * duplicated body pipeline. The presentation path (future NPC packs; none today)
 * instantiates directly through the shared `PackLoaderCache`.
 *
 * AP5.S2 gives placed rig NPCs an IDLE loop: with a `createMixer` factory injected
 * (the same `createThreeRigMixer` the player renderer gets) each rig-sourced NPC is
 * attached to a `RigAnimator`, set to `idle`, and de-phased by a deterministic
 * per-id offset (`idleStaggerSeconds` — an FNV-1a hash of the NPC id, never
 * `Math.random`, so a capture reproduces) so a crowd never breathes in unison.
 * `update(dtMs)` advances them from `Game.frame()`; `clear()`/`dispose()` detach
 * every mixer with its root. Without the factory NPCs render exactly as before, in
 * bind pose — which is what the node tests and any non-visual consumer want.
 *
 * Graceful, never-blank contract:
 *   - resolve `null` (no def presentation AND no rig pack delivered) ⇒ add NOTHING —
 *     no primitive placeholder (the genuine pack-absence baseline, proven by test),
 *   - a build settling after a `clear()`/re-`place()` (zone change / dispose) ⇒ drop
 *     the clone, no orphan (a placer-wide monotonic generation guard — the
 *     `CreatureRenderer`/`PlayerRigRenderer` pattern, applied to a whole zone's set),
 *   - unknown model / null template / load error ⇒ add nothing (the loader's own null
 *     path), never a throw into the frame loop.
 *
 * q-npc-ambient-animations (owner-directed 2026-10-18) replaces that single idle with an
 * AMBIENT set per NPC (ambient.ts): a per-id loop from its set's pool (calm idles, or a work
 * loop — lantern, basket, mug, herald's call, seated mystic), occasional work beats (a
 * smith's hammer), and a greeting + talk loop + turn-to-face while the own avatar walks up
 * (`update(dtMs, observer)` — client-side proximity, nothing new on the wire). The clips
 * ride the NPC-only clips pack `npc-ambient` (`NPC_CLIP_PACK_MODELS`); without it every
 * NPC falls back to the rig's own idle, i.e. exactly the AP5.S2 look.
 *
 * q-npc-props-existing-wiring (owner 2026-10-18) gives a set its props from the shared
 * `NPC_AMBIENT_PROPS` row: HELD items ride the rig's hand bones through the injected
 * `createHeldProps` (Game builds the same `EquipmentRenderer` a remote player's weapon
 * uses), and a STATION (a smith's anvil) stands `forward` metres along the post's facing
 * as a scene sibling. Both are presentation only — no collider, nothing on the wire.
 *
 * Nothing rides the per-tick path — placement keys off the join ack (AD-031); the
 * NPC meshes are bodyless render objects carrying no collision/stats (the world
 * descriptor + colliders are untouched, off the per-tick path by construction).
 * Node-safe like the sibling renderers: the only `three` dependency is the
 * `Object3D` TYPE (erased at runtime); the loader is injected and the scene is an
 * abstract `NpcScene`, so the whole lifecycle runs under `node --test` with fakes
 * (AD-120). Import boundary (AD-122): shared types via `@great-cloud/shared/client`.
 */

import type { Object3D } from 'three';

import {
  DEFAULT_APPEARANCE,
  NPC_AMBIENT_PROPS,
  type NpcAmbientProps,
  type NpcAmbientSet,
  type NpcHeldAssetProp,
  type NpcHeldItemProp,
  type NpcMarkerKind,
  type NpcWire,
  npcMarkerKind,
} from '@great-cloud/shared/client';

import { PackLoaderCache, disposeObject3D, type GltfLoadFn } from '../assets/index.js';
import {
  RigAvatarBuilder,
  mergeClips,
  type RigClip,
  type RigMixerFactory,
} from '../rig-avatar/index.js';
import {
  NpcAmbientDriver,
  ambientSetFor,
  clipNamesOf,
  fnv1a,
  planNpcAmbient,
  type NpcObserver,
} from './ambient.js';
import { isHeldAssetProp, isHeldItemProp, seatHeldAsset } from './heldAssets.js';
import { applyNpcLivery, npcLivery, type LiveryRoot } from './livery.js';
import { resolveNpc, type NpcContent, type NpcResolution } from './resolve.js';
import { markWorldContent } from '../scene/worldContent.js';
import { pointsAnchor, type PendingAssetSink } from '../pendingAssets.js';
import type { AssetUpgradeGate, UpgradeQueue } from '../scene/assetUpgradeGate.js';

/** The marker yaw that turns its face (local +z) back toward a camera looking along yaw. */
export function markerFacingYaw(cameraYaw: number): number {
  return cameraYaw + Math.PI;
}

// What a marker announces and which NPC wears which: ONE rule for the world marker, the HUD
// talk prompt and the minimap (shared/src/quest/guidance.ts, q-quest-marker-glyph-semantics).
export { npcMarkerKind, type NpcMarkerKind };

/** Height of a marker's base above the NPC's feet (world units) — above head and health bars. */
export const NPC_MARKER_HEIGHT = 2.35;

/**
 * The minimal scene slice the placer adds/removes NPC roots on — a `THREE.Scene`
 * structurally. Abstract so the lifecycle runs under `node --test` with a fake.
 */
export interface NpcScene {
  add(object: Object3D): void;
  remove(object: Object3D): void;
}

/** Everything App hands Game so it can place NPCs. */
export interface NpcBridge {
  content: NpcContent;
}

export interface NpcPlacerOptions {
  /** Presentation lookup (npc defs + asset packs + appearance-option catalogue). */
  content: NpcContent;
  /** The scene NPC roots are added to (`Game`'s `THREE.Scene`). */
  scene: NpcScene;
  /** Injected glTF loader (real: `createThreeGltfLoader()`; tests: a stub). */
  gltfLoad: GltfLoadFn;
  /** Static asset HTTP base for delivery URLs (`assetHttpBase()`); '' = same-origin. */
  httpBase?: string;
  /** GPU-resource disposer for a removed mesh (default: three geometry/material dispose). */
  dispose?: (object: Object3D) => void;
  /** Non-fatal warning sink (default `console.warn`). */
  onWarn?: (message: string, error?: unknown) => void;
  /**
   * Injected animation-mixer construction (AP5.S2; real: `createThreeRigMixer()`).
   * Omit to leave NPCs static in bind pose, exactly as before.
   */
  createMixer?: RigMixerFactory;
  /**
   * AD-408: the visual ground height at a world point — the zone's heightfield
   * surface, read when an NPC settles. Absent ⇒ flat ground (y = 0, today).
   * Render-only: the NPC's (x, z) is the wire's, untouched.
   */
  groundHeightAt?: (x: number, z: number) => number;
  /**
   * sprint-assembly-1: build the floating marker an NPC wears (real: a small emissive "?" or
   * "!" from `createNpcMarker`, marker.ts). Omit ⇒ no markers, exactly as before.
   */
  createMarker?: (kind: NpcMarkerKind) => Object3D | null;
  /**
   * q-npc-props-existing-wiring: put a set's held props in a rig NPC's hands (real: an
   * `EquipmentRenderer` on the rig's bone sockets). Omit ⇒ empty hands, exactly as before.
   */
  createHeldProps?: (root: Object3D, held: NpcHeldPropList) => NpcHeldProps | null;
  /**
   * q-npc-props-adoption-and-flame: seat a set's held ASSET props (`NpcHeldAssetProp`, a pack
   * model with a declared grip) on the rig's hand bones, loaded through this placer's own
   * pack loaders. Default `false` ⇒ asset rows are skipped, exactly as before.
   */
  attachHeldAssets?: boolean;
  /**
   * Draw a work station from its textured base (else its LOD0), like the node placer.
   * `Game` passes `fullDetailAssetsActive()`. Default `false` (LOD0).
   */
  useFullDetailBase?: boolean;
  /**
   * q-mobile-first-frame-defer-creatures-and-npc-clips: place rig NPCs on the body's own
   * clips and fetch the clips-only packs (`NPC_CLIP_PACK_MODELS`) only at
   * `releaseClipPacks()`, adding them to each NPC's live mixer (q-npc-pack-clips-without-
   * restart: its idle keeps playing; the set's clips take over at the next transition).
   * Default false.
   */
  deferClipPacks?: boolean;
  /**
   * Where in-flight loads are reported (q-asset-readiness-loading-screen), anchored where
   * they will appear, so the loading screen holds until those in view have arrived.
   */
  pending?: PendingAssetSink;
  /**
   * q-desktop-proxy-first-asset-upgrade: while `gate` is closed a full-detail station stands
   * as its LOD0 and its textured base joins `queue`, swapping in after the loading screen.
   * Absent ⇒ the textured base loads at once, as before.
   */
  upgrades?: { gate: Pick<AssetUpgradeGate, 'released'>; queue: Pick<UpgradeQueue, 'add'> };
}

/**
 * The ITEM rows of one ambient set's held props — what `createHeldProps` draws through the
 * equipment renderer. Asset rows (`NpcHeldAssetProp`) are the placer's own (`attachHeldAssets`).
 */
export type NpcHeldPropList = readonly NpcHeldItemProp[];

/** A live set of held props on one NPC; the placer disposes it with the NPC. */
export interface NpcHeldProps {
  dispose(): void;
}

/**
 * The clips-only packs NPCs load beside the body (q-npc-ambient-animations): the ambient
 * set clips. NOT the player's attack combos — an NPC never swings.
 */
export const NPC_CLIP_PACK_MODELS: readonly string[] = ['npc-ambient'];

/**
 * Upper bound of the per-NPC idle phase offset, seconds. Any spread of a few
 * seconds de-synchronises a crowd whose idle loop is several seconds long; the
 * exact loop length is a per-rig fact the node-safe placer deliberately does not
 * know (the mixer wraps a longer seek around anyway).
 */
export const NPC_IDLE_STAGGER_MAX_SECONDS = 4;

/**
 * Deterministic idle phase offset for one NPC, in `[0, maxSeconds)`: FNV-1a (32-bit)
 * over the id, mapped to the range. The same NPC always gets the same phase (no
 * `Math.random` — reproducible captures, AD-120), and neighbouring ids spread
 * across the whole range rather than clustering.
 */
export function idleStaggerSeconds(
  npcId: string,
  maxSeconds: number = NPC_IDLE_STAGGER_MAX_SECONDS,
): number {
  return (fnv1a(npcId) / 0x1_0000_0000) * maxSeconds;
}

/** One placed NPC: its scene root, its marker (if any) and, when animated, its ambient driver. */
interface PlacedNpc {
  id: string;
  root: Object3D;
  marker: Object3D | null;
  /** What `marker` announces, and the wire row it sits over (AD-867 re-marks on a quest change). */
  markerKind: NpcMarkerKind | null;
  npc: NpcWire;
  ambient: NpcAmbientDriver | null;
  held: NpcHeldProps | null;
  /** Its wire row + clips, kept while it still owes the deferred clip packs (else null). */
  owesClipPacks: { npc: NpcWire; clips: readonly RigClip[] } | null;
}

/** A settled instantiation: the root plus whatever clips it carries (`[]` when none). */
interface NpcInstance {
  root: Object3D;
  clips: readonly RigClip[];
  /** Built under the first-frame hold, without the clips-only packs. */
  skippedClipPacks: boolean;
}

function defaultWarn(message: string, error?: unknown): void {
  console.warn(`[npcs] ${message}`, error ?? '');
}

/**
 * Places a zone's NPCs. Create it once against the content bridge, `place()` the
 * join ack's `npcs`, and `dispose()` on scene teardown. The rig fallback shares one
 * `RigAvatarBuilder` (and thus one pack-loader/template cache) across every NPC —
 * all humanoid NPCs share the single `player-rig-v1` pack.
 */
export class NpcPlacer {
  private readonly content: NpcContent;
  private readonly scene: NpcScene;
  /** Rig fallback — the S1 builder, reused verbatim (default appearance per NPC). */
  private readonly rigBuilder: RigAvatarBuilder;
  /** Presentation path — direct instantiate from a def's own bound pack (none today). */
  private readonly presentationLoaders: PackLoaderCache;
  private readonly disposeObject: (object: Object3D) => void;
  private readonly onWarn: (message: string, error?: unknown) => void;
  /** Ambient playback mixers; `null` when no factory was injected (static NPCs). */
  private readonly createMixer: RigMixerFactory | null;

  /** The settled NPCs currently in the scene. */
  private placed: PlacedNpc[] = [];
  /** NPC ids the player's quest log is ready to turn in at (AD-867; `setQuestMarkers`). */
  private turnIn: ReadonlySet<string> = new Set();
  /** NPC ids with something for the player (offer / lore); null ⇒ the questgiver role decides. */
  private offer: ReadonlySet<string> | null = null;
  /** Bumped on every `place()`/`clear()`; a build settling at a stale value is dropped. */
  private generation = 0;
  private disposed = false;
  private readonly groundHeightAt: (x: number, z: number) => number;
  private readonly createMarker: ((kind: NpcMarkerKind) => Object3D | null) | null;
  private readonly createHeldProps:
    | ((root: Object3D, held: NpcHeldPropList) => NpcHeldProps | null)
    | null;
  /** Settled work stations (scene siblings), removed + disposed on `clear()`. */
  private stations: Object3D[] = [];
  private readonly useFullDetailBase: boolean;
  private readonly upgrades: NonNullable<NpcPlacerOptions['upgrades']> | null;
  private readonly attachHeldAssets: boolean;
  /** Rig builds skip the clip packs until `releaseClipPacks()`. */
  private clipPacksHeld: boolean;
  private readonly pending: PendingAssetSink | null;
  /** The released clip packs' clips, once fetched. */
  private packClips: Promise<readonly RigClip[]> | null = null;

  constructor(options: NpcPlacerOptions) {
    this.content = options.content;
    this.scene = options.scene;
    this.rigBuilder = new RigAvatarBuilder({
      content: options.content,
      gltfLoad: options.gltfLoad,
      httpBase: options.httpBase,
      dispose: options.dispose,
      onWarn: options.onWarn,
      clipPackModels: NPC_CLIP_PACK_MODELS,
      // Held packs ⇒ the low tier: NPCs share the player's lean first-frame body.
      leanBody: options.deferClipPacks ?? false,
    });
    this.presentationLoaders = new PackLoaderCache({
      gltfLoad: options.gltfLoad,
      httpBase: options.httpBase,
      onWarn: options.onWarn ?? defaultWarn,
    });
    this.disposeObject = options.dispose ?? disposeObject3D;
    this.onWarn = options.onWarn ?? defaultWarn;
    this.groundHeightAt = options.groundHeightAt ?? (() => 0);
    this.createMarker = options.createMarker ?? null;
    this.createMixer = options.createMixer ?? null;
    this.createHeldProps = options.createHeldProps ?? null;
    this.useFullDetailBase = options.useFullDetailBase ?? false;
    this.attachHeldAssets = options.attachHeldAssets ?? false;
    this.clipPacksHeld = options.deferClipPacks ?? false;
    this.pending = options.pending ?? null;
    this.upgrades = options.upgrades ?? null;
  }

  /**
   * Lift the clip-pack hold: fetch the packs once, then add their clips to every NPC placed
   * without them (on its live mixer, nothing restarts); later placements build with them.
   * Idempotent.
   */
  releaseClipPacks(): void {
    if (!this.clipPacksHeld || this.disposed) return;
    this.clipPacksHeld = false;
    for (const placed of this.placed) this.completeClipPacks(placed);
  }

  /**
   * Give one NPC the released packs' clips once they land (a no-op if owed none).
   * q-npc-pack-clips-without-restart: an NPC already animating keeps its mixer and its
   * driver. The clips join the live mixer and the driver re-plans in place
   * (`NpcAmbientDriver.addClips`), so the idle on screen does not restart. Building a new
   * mixer here restarted every NPC's idle once on the low tier. An NPC with no ambient
   * (its body clips gave no loop) has nothing to restart, so it starts fresh on the
   * merged set.
   */
  private completeClipPacks(placed: PlacedNpc): void {
    const owed = placed.owesClipPacks;
    if (owed === null) return;
    placed.owesClipPacks = null;
    this.packClips ??= this.rigBuilder.clipPackClips();
    void this.packClips
      .then((extra) => {
        if (this.disposed || !this.placed.includes(placed) || extra.length === 0) return;
        const clips = mergeClips<RigClip>(owed.clips, extra);
        if (clips.length === owed.clips.length) return;
        const ambient = placed.ambient;
        if (ambient === null) {
          placed.ambient = this.startAmbient(owed.npc, placed.root, clips);
          return;
        }
        try {
          ambient.addClips(clips.slice(owed.clips.length), clipNamesOf(clips));
        } catch (error) {
          // The driver changes nothing when the mixer refuses the clips: body clips stay.
          this.onWarn(`npc '${placed.id}' adding the clip packs failed; body clips stay`, error);
        }
      })
      .catch((error: unknown) => {
        this.onWarn(`npc '${placed.id}' deferred clip packs failed; body clips stay`, error);
      });
  }

  /**
   * Place the zone's NPCs (join-only). Clears any prior placement first (a zone
   * change re-runs this off `onReady`), then resolves + instantiates each `NpcWire`
   * asynchronously and stands its mesh at (x, 0, z). An unresolvable NPC adds
   * NOTHING (no placeholder). Safe to call before the content pack loads — every NPC
   * then resolves to `null` and nothing is placed; `Game` re-`place()`s once the pack
   * arrives (`onContentPack`), exactly like the world scene.
   */
  place(npcs: readonly NpcWire[]): void {
    this.clear(); // supersedes any in-flight build + removes prior NPCs
    if (this.disposed) return;
    const generation = this.generation;
    for (const npc of npcs) {
      const resolution = resolveNpc(this.content, npc.id);
      if (resolution === null) continue; // render nothing (no scene object)
      const props = NPC_AMBIENT_PROPS[this.ambientSet(npc)];
      if (props?.station !== undefined) this.placeStation(npc, props.station, generation);
      const build = this.instantiate(resolution, npc);
      void (
        this.pending?.track(
          `npc: ${npc.id}`,
          pointsAnchor([{ x: npc.x, y: this.groundHeightAt(npc.x, npc.z), z: npc.z }]),
          build,
        ) ?? build
      )
        .then((instance) => {
          if (generation !== this.generation || this.disposed) {
            // Superseded (zone change / dispose before settle) ⇒ dispose, no orphan.
            if (instance !== null) this.disposeObject(instance.root);
            return;
          }
          if (instance === null) return; // unknown model / null template / load error ⇒ nothing
          const { root } = instance;
          // static placement; no facing on the wire. AD-408: seated on the ground.
          root.position.set(npc.x, this.groundHeightAt(npc.x, npc.z), npc.z);
          // sprint-assembly-1: a questgiver carries a floating marker. It is a SCENE
          // sibling in world units, not a child of the rig root: the imported rig root
          // carries its own scale/axis transform, which tilted and flung a child marker.
          const markerKind = npcMarkerKind(npc.id, npc.roles, this.turnIn, this.offer);
          const marker = this.buildMarker(markerKind, npc, root.position.y);
          this.scene.add(markWorldContent(root));
          const placed: PlacedNpc = {
            id: npc.id,
            root,
            marker,
            markerKind,
            npc,
            ambient: this.startAmbient(npc, root, instance.clips),
            held: resolution.source === 'rig' ? this.attachHeld(npc, root, props?.held) : null,
            owesClipPacks: instance.skippedClipPacks ? { npc, clips: instance.clips } : null,
          };
          this.placed.push(placed);
          // Built during the hold but settled after it lifted: complete it now.
          if (!this.clipPacksHeld) this.completeClipPacks(placed);
        })
        .catch((error: unknown) => {
          // The builder/loader degrade failures to null; guard an unexpected throw so a
          // bad placement never breaks the frame loop.
          this.onWarn(`npc '${npc.id}' placement failed`, error);
        });
    }
  }

  /**
   * Advance every placed NPC's ambient animation. `dtMs` is the frame delta in MILLISECONDS;
   * `observer` is the own avatar's rendered (x, z), or null/absent before it exists (then
   * nobody greets or turns). Never throws into the frame loop: a failing NPC is dropped to
   * a static pose with a warning.
   */
  /**
   * AD-867: turn every "?" marker to face the camera (`cameraYaw`, the orbit yaw: the view looks
   * along (sin yaw, cos yaw)). The "!" is symmetric and stays put; the "?" is not, and from
   * behind it read back to front. Keyed on the marker's own `userData.facesCamera` (set where the glyph is built),
   * never on the kind, so a kind that changes glyph keeps facing right (q-quest-marker-glyph-semantics).
   */
  faceMarkers(cameraYaw: number): void {
    for (const placed of this.placed) {
      if (placed.markerKind === null || placed.marker === null) continue;
      if (
        (placed.marker as { userData?: { facesCamera?: boolean } }).userData?.facesCamera !== true
      )
        continue;
      const rotation = (placed.marker as { rotation?: { y: number } }).rotation;
      if (rotation !== undefined) rotation.y = markerFacingYaw(cameraYaw);
    }
  }

  update(dtMs: number, observer: NpcObserver | null = null): void {
    if (this.createMixer === null || this.placed.length === 0) return;
    const dtSeconds = dtMs / 1000;
    for (const placed of this.placed) {
      const ambient = placed.ambient;
      if (ambient === null) continue;
      try {
        ambient.update(dtSeconds, observer);
        const rotation = (placed.root as { rotation?: { y: number } }).rotation;
        if (rotation !== undefined && ambient.plan.facesPlayer) rotation.y = ambient.yaw;
      } catch (error) {
        this.onWarn(`npc '${placed.id}' ambient update failed; dropping its animation`, error);
        placed.ambient = null;
        try {
          ambient.stop();
        } catch {
          // already failing; the warning above is the record
        }
      }
    }
  }

  /** The ambient set + mode per placed NPC id (tests + `__gcSceneDump`). */
  ambientModes(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const placed of this.placed) {
      if (placed.ambient === null) continue;
      out[placed.id] = `${placed.ambient.plan.set}:${placed.ambient.currentMode}`;
    }
    return out;
  }

  /**
   * Instantiate a resolution's root. Rig fallback: the shared `RigAvatarBuilder`
   * (default appearance; wardrobe parented under the returned root, clips from the
   * rig). Presentation: a direct load from the def's own pack (no clips — an NPC
   * pack with its own idle is a follow-up; none exists today). `null` ⇒ the placer
   * adds nothing.
   */
  private async instantiate(resolution: NpcResolution, npc: NpcWire): Promise<NpcInstance | null> {
    if (resolution.source === 'rig') {
      // sprint-assembly-1 (AD-647): a rig NPC wears its faction's livery and an
      // id-picked build + skin tone, so it never reads as a copy of the player.
      const livery = npcLivery(npc.id, this.content.npc(npc.id)?.faction);
      const skippedClipPacks = this.clipPacksHeld;
      const avatar = await this.rigBuilder.build(
        { ...DEFAULT_APPEARANCE, bodyType: livery.bodyType },
        { skipClipPacks: skippedClipPacks },
      );
      if (avatar === null) return null;
      applyNpcLivery(avatar.root as unknown as LiveryRoot, livery);
      return { root: avatar.root, clips: avatar.clips, skippedClipPacks };
    }
    const root = await this.presentationLoaders
      .loaderFor(resolution.pack)
      .instantiate(resolution.model, 0);
    return root === null ? null : { root, clips: [], skippedClipPacks: false };
  }

  /**
   * Start a placed NPC's ambient loop, de-phased by its deterministic offset. Returns the
   * driver, or `null` when the NPC stays static (no factory, no clips, a rig with no
   * usable loop or idle, or a mixer that could not be built — the never-blank rule: bind
   * pose, no throw).
   */
  private startAmbient(
    npc: NpcWire,
    root: Object3D,
    clips: readonly RigClip[],
  ): NpcAmbientDriver | null {
    if (this.createMixer === null || clips.length === 0) return null;
    const set = this.ambientSet(npc);
    const plan = planNpcAmbient(npc.id, set, clipNamesOf(clips));
    if (plan === null) return null;
    try {
      const mixer = this.createMixer(root, clips);
      if (mixer === null) return null;
      const driver = new NpcAmbientDriver({ npcId: npc.id, plan, mixer, x: npc.x, z: npc.z });
      driver.start(idleStaggerSeconds(npc.id));
      return driver;
    } catch (error) {
      this.onWarn(`npc '${npc.id}' ambient start failed; it stays static`, error);
      return null;
    }
  }

  /** The ambient set an NPC plays: its content `ambient`, else its role default. */
  private ambientSet(npc: NpcWire): NpcAmbientSet {
    const def = this.content.npc(npc.id);
    return ambientSetFor({ ambient: def?.ambient, roles: def?.roles ?? npc.roles });
  }

  /**
   * Hand a rig NPC its set's held props (none ⇒ null): ITEM rows through the injected
   * `createHeldProps`, ASSET rows through this placer's pack loaders (`attachHeldAssets`).
   * A failure leaves that hand empty.
   */
  private attachHeld(
    npc: NpcWire,
    root: Object3D,
    held: NpcAmbientProps['held'],
  ): NpcHeldProps | null {
    if (held === undefined || held.length === 0) return null;
    const parts: NpcHeldProps[] = [];
    const items = held.filter(isHeldItemProp);
    if (items.length > 0 && this.createHeldProps !== null) {
      try {
        const part = this.createHeldProps(root, items);
        if (part !== null) parts.push(part);
      } catch (error) {
        this.onWarn(`npc '${npc.id}' held props failed; empty hands`, error);
      }
    }
    const assets = held.filter(isHeldAssetProp);
    if (assets.length > 0 && this.attachHeldAssets) parts.push(this.heldAssets(npc, root, assets));
    if (parts.length === 0) return null;
    return {
      dispose: () => {
        for (const part of parts) part.dispose();
      },
    };
  }

  /**
   * Load each asset row's model (the textured base when full detail is on, as stations do)
   * and seat it on its hand bone with the declared grip. A prop settling after its NPC was
   * cleared is dropped; an unknown pack/model or a rig without the bone adds nothing.
   */
  private heldAssets(
    npc: NpcWire,
    root: Object3D,
    rows: readonly NpcHeldAssetProp[],
  ): NpcHeldProps {
    const seated: Object3D[] = [];
    let released = false;
    const bone = (name: string): Object3D | null =>
      typeof root.getObjectByName === 'function' ? (root.getObjectByName(name) ?? null) : null;
    for (const row of rows) {
      const pack = this.content.assetPack(row.assetPack);
      if (pack === undefined) continue;
      void this.presentationLoaders
        .loaderFor(pack)
        .instantiatePreferred(row.model, this.useFullDetailBase)
        .then((prop) => {
          if (prop === null) return;
          if (released || this.disposed) {
            this.disposeObject(prop);
            return;
          }
          if (!seatHeldAsset(prop, bone, row)) {
            this.onWarn(`npc '${npc.id}' has no ${row.slot} bone for '${row.model}'`);
            this.disposeObject(prop);
            return;
          }
          seated.push(prop);
        })
        .catch((error: unknown) => {
          this.onWarn(`npc '${npc.id}' held '${row.model}' failed`, error);
        });
    }
    return {
      dispose: () => {
        released = true;
        for (const prop of seated) {
          prop.parent?.remove(prop);
          this.disposeObject(prop);
        }
        seated.length = 0;
      },
    };
  }

  /**
   * Stand a set's work station `forward` metres along the NPC's home facing (+Z, the
   * facing the placer leaves an NPC at), seated on the ground. An undelivered pack or a
   * failed load adds nothing; a build settling after a `clear()` is dropped.
   */
  private placeStation(
    npc: NpcWire,
    station: NonNullable<NpcAmbientProps['station']>,
    generation: number,
  ): void {
    const pack = this.content.assetPack(station.assetPack);
    if (pack === undefined) return;
    const x = npc.x;
    const z = npc.z + station.forward;
    const loader = this.presentationLoaders.loaderFor(pack);
    // Proxy-first: LOD0 now, the textured base once the loading screen has lifted.
    const hold = this.useFullDetailBase && this.upgrades !== null && !this.upgrades.gate.released;
    const load = loader.instantiatePreferred(station.model, this.useFullDetailBase && !hold);
    const anchor = pointsAnchor([{ x, y: this.groundHeightAt(x, z), z }]);
    void (this.pending?.track(`npc station: ${station.model}`, anchor, load) ?? load)
      .then((root) => {
        if (root === null) return;
        if (generation !== this.generation || this.disposed) {
          this.disposeObject(root);
          return;
        }
        root.position.set(x, this.groundHeightAt(x, z), z);
        const rotation = (root as { rotation?: { y: number } }).rotation;
        if (rotation !== undefined) rotation.y = station.yaw ?? 0;
        this.scene.add(markWorldContent(root));
        this.stations.push(root);
        if (hold)
          this.upgrades?.queue.add(x, z, () =>
            loader.instantiateBase(station.model).then((textured) => {
              if (textured === null) return; // no textured base: LOD0 stays
              const at = this.stations.indexOf(root);
              if (at < 0 || generation !== this.generation || this.disposed) {
                this.disposeObject(textured);
                return;
              }
              textured.position.set(root.position.x, root.position.y, root.position.z);
              const turn = (textured as { rotation?: { y: number } }).rotation;
              if (turn !== undefined) turn.y = station.yaw ?? 0;
              this.scene.add(markWorldContent(textured));
              this.scene.remove(root);
              this.disposeObject(root);
              this.stations[at] = textured;
            }),
          );
      })
      .catch((error: unknown) => {
        this.onWarn(`npc '${npc.id}' station '${station.model}' failed`, error);
      });
  }

  /** A floating marker of `kind` over `npc`, added to the scene; null for none. */
  private buildMarker(kind: NpcMarkerKind | null, npc: NpcWire, groundY: number): Object3D | null {
    if (kind === null || this.createMarker === null) return null;
    const marker = this.createMarker(kind);
    if (marker === null) return null;
    marker.position.set(npc.x, groundY + NPC_MARKER_HEIGHT, npc.z);
    this.scene.add(markWorldContent(marker)); // AD-705: hidden while a run is drawn
    return marker;
  }

  /**
   * AD-867 + q-quest-marker-glyph-semantics: the NPCs the player's quest log is ready to turn in
   * at (status `complete`, by the quest's turn-in NPC) and the NPCs with something for the player
   * (`offerNpcSet`: a quest to take or lore to ask about; null ⇒ the questgiver role decides).
   * Each placed NPC whose marker kind changes is re-marked at once; NPCs placed later read the
   * same sets. Generic: driven by quest state, never by a quest id.
   */
  setQuestMarkers(turnIn: Iterable<string>, offer: Iterable<string> | null): void {
    this.turnIn = new Set(turnIn);
    this.offer = offer === null ? null : new Set(offer);
    for (const placed of this.placed) {
      const kind = npcMarkerKind(placed.npc.id, placed.npc.roles, this.turnIn, this.offer);
      if (kind === placed.markerKind) continue;
      if (placed.marker !== null) {
        this.scene.remove(placed.marker);
        this.disposeObject(placed.marker);
      }
      placed.marker = this.buildMarker(kind, placed.npc, placed.root.position.y);
      placed.markerKind = kind;
    }
  }

  /** The marker kind each placed NPC wears now (tests, the DEV dump). */
  markerKinds(): Map<string, NpcMarkerKind | null> {
    return new Map(this.placed.map((p) => [p.id, p.markerKind]));
  }

  /**
   * Remove + dispose every placed NPC (detaching its idle mixer first) and
   * supersede any in-flight build (a zone change re-place, or teardown). Keeps the
   * builder/loader TEMPLATE caches (a re-place reuses them); `dispose()` drops those.
   */
  clear(): void {
    this.generation += 1;
    for (const { root, marker, ambient, held } of this.placed) {
      if (held !== null) {
        try {
          held.dispose();
        } catch (error) {
          this.onWarn('npc held props dispose failed', error);
        }
      }
      if (ambient !== null) {
        try {
          ambient.stop(); // no mixer outlives its root
        } catch (error) {
          this.onWarn('npc mixer stop failed', error);
        }
      }
      this.scene.remove(root);
      this.disposeObject(root);
      if (marker !== null) {
        this.scene.remove(marker);
        this.disposeObject(marker);
      }
    }
    this.placed = [];
    for (const station of this.stations) {
      this.scene.remove(station);
      this.disposeObject(station);
    }
    this.stations = [];
  }

  /** Teardown: clear placements, stop every mixer + drop the builder/loader caches. */
  dispose(): void {
    this.disposed = true;
    this.clear();
    this.rigBuilder.dispose();
    this.presentationLoaders.clear();
  }
}
